import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import {selectTab} from '@Components/tabs/tabs-reducer';
import type {TabClickHandler, TabItemContentStorage} from '@Components/tabs';
import {Tabs, useSelectedTab} from '@Components/tabs';
import {UploadsMediaSubTab, UploadsMediaType, USER_UPLOADS_MEDIA_SUB_TABS, USER_UPLOADS_MEDIA_TABS} from '@Panels/user-media-panel/user-media-panel.types';
import {isUserLoggedIn} from '@Libraries/user.library';
import {Button, Size, Type} from '@Components/button';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {openAuthenticateModal} from '@Modals/authenticate-modal';
import {useLeftRightScrollShadow} from '@Hooks/useLeftRightScrollShadow';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './tabs-section.module.scss';

interface TabsSectionProps {
  isSmallScreen: boolean;
  defaultTab: UploadsMediaType;
  mediaTypesToShow: Array<UploadsMediaType>;
  defaultActiveSubTab?: UploadsMediaSubTab;
}

export function TabsSection({isSmallScreen = false, mediaTypesToShow = [], defaultTab = UploadsMediaType.IMAGE, defaultActiveSubTab = UploadsMediaSubTab.UPLOADS}: TabsSectionProps): ReactElement {
  const activeTab = useSelectedTab(USER_UPLOADS_MEDIA_TABS, defaultTab) as UploadsMediaType;
  const containerRef = useRef<HTMLDivElement>(null);
  const {leftShadow, rightShadow} = useLeftRightScrollShadow(containerRef, false);
  const activeSubtab = useSelectedTab(USER_UPLOADS_MEDIA_SUB_TABS, defaultActiveSubTab) as UploadsMediaSubTab;
  const unlinkedItems = useAppSelector((state) => {
    return state.mediaUploads.unlinkedMedia;
  });
  const dispatch = useAppDispatch();

  const getLoginButton = (): ReactElement | null => {
    if (!unlinkedItems.length) {
      return null;
    }
    return (
      <Button
        text={window.i18next.t('pmwjs_user_login_action')}
        icon="icon-sign-in"
        size={Size.SMALL}
        isFullWidth={isSmallScreen}
        type={Type.SECONDARY}
        onClick={(): void => {
          openAuthenticateModal({
            fbPermissions: '',
            signupSource: 'default',
          });
        }}
      />
    );
  };

  const getPrimaryTabItems = (): Array<TabItemContentStorage> => {
    const tabs = [];
    if (mediaTypesToShow.includes(UploadsMediaType.IMAGE)) {
      tabs.push({
        tabKey: UploadsMediaType.IMAGE,
        text: window.i18next.t('pmwjs_my_photos'),
      });
    }
    if (mediaTypesToShow.includes(UploadsMediaType.VIDEO)) {
      tabs.push({
        tabKey: UploadsMediaType.VIDEO,
        text: window.i18next.t('pmwjs_my_videos'),
      });
    }
    if (mediaTypesToShow.includes(UploadsMediaType.AUDIO)) {
      tabs.push({
        tabKey: UploadsMediaType.AUDIO,
        text: window.i18next.t('pmwjs_my_audio'),
      });
    }
    return tabs;
  };

  const getPrimaryTabs = (): ReactElement | null => {
    if (isSmallScreen) {
      return null;
    }
    if (mediaTypesToShow.length === 1) {
      return null;
    }
    return <Tabs id={USER_UPLOADS_MEDIA_TABS} className="spacing-m-0 spacing-p-0 nwr" tabItems={getPrimaryTabItems()} selectedTab={activeTab} onTabClick={onTabClick} />;
  };

  const onTabClick: TabClickHandler = (tabId): void => {
    const tab = tabId as UploadsMediaType;
    switch (activeSubtab) {
      case UploadsMediaSubTab.LOGO:
      case UploadsMediaSubTab.AI_IMAGE:
        if (tab !== UploadsMediaType.IMAGE) {
          dispatch(selectTab(USER_UPLOADS_MEDIA_SUB_TABS, UploadsMediaSubTab.UPLOADS));
        }
        break;
      case UploadsMediaSubTab.AI_VOICE:
        if (tab !== UploadsMediaType.AUDIO) {
          dispatch(selectTab(USER_UPLOADS_MEDIA_SUB_TABS, UploadsMediaSubTab.UPLOADS));
        }
        break;
      case UploadsMediaSubTab.UPLOADS:
      case UploadsMediaSubTab.COLLECTION:
        break;
      default:
        throw new Error(`Unhandled active sub tab: ${activeTab}`);
    }
  };

  const getPill = (text: string, subtabType: UploadsMediaSubTab): ReactElement => {
    return (
      <PillButton
        key={subtabType}
        size={isSmallScreen ? PillButtonSize.SMALL : PillButtonSize.MEDIUM}
        isSelected={activeSubtab === subtabType}
        text={text}
        retainSizeOnSelection
        onClick={(): void => {
          dispatch(selectTab(USER_UPLOADS_MEDIA_SUB_TABS, subtabType));
        }}
      />
    );
  };

  const getImageMediaTypeSpecificSubtabs = (): ReactElement => {
    return (
      <>
        {getPill(window.i18next.t('pmwjs_logos'), UploadsMediaSubTab.LOGO)}
        {getPill(window.i18next.t('pmwjs_ai_generations'), UploadsMediaSubTab.AI_IMAGE)}
      </>
    );
  };

  const getAudioMediaTypeSpecificSubtabs = (): ReactElement => {
    return getPill(window.i18next.t('pmwjs_ai_generations'), UploadsMediaSubTab.AI_VOICE);
  };

  const getMediaTypeSpecificSubtabs = (): ReactElement | null => {
    switch (activeTab) {
      case UploadsMediaType.VIDEO:
      case UploadsMediaType.IMAGE_BACKGROUND:
        return null;
      case UploadsMediaType.IMAGE:
        return getImageMediaTypeSpecificSubtabs();
      case UploadsMediaType.AUDIO:
        return getAudioMediaTypeSpecificSubtabs();
      default:
        throw new Error(`Unhandled active tab: ${activeTab}`);
    }
  };

  const getSubPills = (): ReactElement | null => {
    if (!isUserLoggedIn()) {
      return null;
    }
    return (
      <div
        ref={containerRef}
        className={`flexbox ${styles.pills} ${isSmallScreen || mediaTypesToShow.length === 1 ? '' : 'spacing-p-t-5'} ${rightShadow ? styles.rightShadow : ''} ${leftShadow ? styles.leftShadow : ''}`}
      >
        {getPill(window.i18next.t('pmwjs_uploaded'), UploadsMediaSubTab.UPLOADS)}
        {getPill(window.i18next.t('pmwjs_collections'), UploadsMediaSubTab.COLLECTION)}
        {getMediaTypeSpecificSubtabs()}
      </div>
    );
  };

  return (
    <div className="flex-v-row _full-width spacing-p-t-6">
      <div className="flex-row-justify-between">
        {getPrimaryTabs()}
        {isUserLoggedIn() ? null : getLoginButton()}
      </div>
      {getSubPills()}
    </div>
  );
}
